import React from 'react';
import { fmt, fmtCompact, rateStatus, SEQUENTIAL } from './vizTokens';

/**
 * The one number the dashboard leads with. Exactly one per view.
 *
 * Proportional figures on purpose: `tabular-nums` gives every digit the width of
 * a zero, which makes a large number look loose. Tabular is reserved for columns
 * that must align vertically (table rows, axis ticks).
 */
export const HeroFigure = ({ label, value, caption }) => (
  <div>
    <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
    <p className="text-5xl font-semibold text-slate-900 leading-none tracking-tight">
      {fmt(value)}
    </p>
    {caption && <p className="text-sm text-slate-400 mt-2">{caption}</p>}
  </div>
);

/**
 * Meter — one ratio against a limit.
 *
 * The fill carries severity and the unfilled track is a lighter step of the same
 * ramp, so the state reads across the whole bar rather than only the filled part.
 * The severity word beside it is what actually carries meaning: colour alone
 * never does.
 */
export const Meter = ({ label, rate }) => {
  const pct = Math.max(0, Math.min(100, rate ?? 0));
  const status = rateStatus(pct);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        <span className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold text-slate-900">{pct}%</span>
          <span className="text-xs font-medium text-slate-500">{status.label}</span>
        </span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: `${status.color}1f` }}
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${pct}% — ${status.label}`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, backgroundColor: status.color }}
        />
      </div>
    </div>
  );
};

/**
 * Stat tile: label · value · optional delta against a named period.
 *
 * `tone` paints only the small leading dot and the delta, never the value text —
 * a figure wearing a status colour competes with the hero and reads as decoration.
 */
export const StatTile = ({ label, value, delta, deltaLabel, tone, hint }) => {
  const hasDelta = delta != null && delta !== 0;
  const positive = (delta ?? 0) > 0;

  return (
    <div className="rounded-xl border border-slate-100 bg-white px-4 py-3.5">
      <div className="flex items-center gap-2 mb-1.5">
        {tone && (
          <span
            aria-hidden="true"
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: tone }}
          />
        )}
        <p className="text-xs font-medium text-slate-500 truncate">{label}</p>
      </div>
      <p className="text-xl font-semibold text-slate-900 leading-none">{fmtCompact(value)}</p>
      {hasDelta ? (
        <p className="text-xs text-slate-500 mt-1.5">
          <span className="font-medium text-slate-600">
            {positive ? '+' : '−'}{fmt(Math.abs(delta))}
          </span>
          {deltaLabel ? ` ${deltaLabel}` : ''}
        </p>
      ) : (
        hint && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>
      )}
    </div>
  );
};

/** Compact sequential bar used inside dense list rows (e.g. by-site). */
export const InlineBar = ({ value, total }) => (
  <div
    className="h-1.5 rounded-full overflow-hidden"
    style={{ backgroundColor: SEQUENTIAL.track }}
  >
    <div
      className="h-full rounded-full"
      style={{
        width: `${total > 0 ? (value / total) * 100 : 0}%`,
        backgroundColor: SEQUENTIAL.base,
      }}
    />
  </div>
);
